import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { GuestLoginResponse } from './models/guestlogin';
import { environment } from 'environments/environment';
import { AppConsts } from '@shared/AppConsts';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { TokenService } from 'abp-ng2-module';
import { AuthChangeSignal } from '@shared/helpers/AuthChangeSignal';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // private userKey = 'loggedInUser'; // For persistence in localStorage
  // private userSubject: BehaviorSubject<GuestLoginResponse | null>;
  remoteServiceBaseUrl = AppConsts.remoteServiceBaseUrl;

  constructor(private http: HttpClient, private _appSessionService: AppSessionService, private _tokenService: TokenService) {
  }

  // Perform login call and handle token storage
  webfacingLogin(calculationTypeEnum: number): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-token': environment.token // direct token in header, not as Bearer
    });

    const uuid = this.getOrGenerateUUID(calculationTypeEnum);

    return this.http.get(`${this.remoteServiceBaseUrl}/api/TokenAuth/WebFacingLogin`, {
      headers,
      params: { uuid, calculationTypeEnum },
      withCredentials: true
    });
  }

  getOrGenerateUUID(calculationTypeEnum: number) {
    let uuid = localStorage.getItem('webfacing.uniqueipuser_' + calculationTypeEnum.toString());
    if (!uuid) {
      uuid = this.generateCalculationName(calculationTypeEnum);
      localStorage.setItem('webfacing.uniqueipuser_' + calculationTypeEnum.toString(), uuid);
    }
    return uuid;
  }

  // Helper to generate a UUID
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private generateCalculationName(calculationType: number): string {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 16)           // keep YYYY-MM-DDTHH:MM
      .replace(/[-:T]/g, '');
    let calcName = 'Cable Sizing';
    switch (calculationType) {
      case 0:
      case 1:
      case 3:
      case 9:
        break;
      case 4:
        calcName = 'Maximum Demand';
        break;
      case 5:
        calcName = 'Protection';
        break;
      case 6:
        calcName = 'Cable Pulling';
        break;
      case 7:
        calcName = 'LV Network';
        break;
      case 8:
        calcName = 'Arc Flash';
        break;
      default:
        break;
    }
    return `${calcName}_${timestamp}${this.randomAlphanumeric(3)}`;
  }

  private randomAlphanumeric(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Save login response to cookies and local storage
  storeLoginData(calculationTypeEnum: number, data: GuestLoginResponse) {
    let tokenExpireDate = (new Date(new Date().getTime() + 1000 * data.expireInSeconds));
    const cookieName = environment.production ? 'Abp.AuthToken' : 'Abp.DevAuthToken';
    document.cookie = `${cookieName}=${data.token};expires=${tokenExpireDate};domain=${environment.LoginCookieDomain};path=/`;
    AuthChangeSignal.notify();
    localStorage.setItem('webfacing.projectid_' + calculationTypeEnum.toString(), data.projectId.toString());
    localStorage.setItem('webfacing.projectname_' + calculationTypeEnum.toString(), data.projectName);
    localStorage.setItem('webfacing.calculationid_' + calculationTypeEnum.toString(), data.calculationId.toString());
    localStorage.setItem('webfacing.calculationname_' + calculationTypeEnum.toString(), data.calculationName);
  }


  // Helper methods for cookies
  setCookie(name: string, value: string, date: Date) {
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
  }

  getCookie(name: string) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  // // Setter for updating user details
  // setUser(userData: any): void {
  //   this.userSubject.next(userData); // Update the BehaviorSubject
  //   localStorage.setItem(this.userKey, JSON.stringify(userData)); // Persist in localStorage
  // }

  // Check if token is already stored in cookies
  isLoggedIn(): boolean {
    return !!this._appSessionService.user;
  }

  // Validate if the guest login token is expired or not
  ValidateWebFacingData(projectId: any, calculationId: any, calculationTypeEnum: number): Observable<any> {
    const cookieName = environment.production ? 'Abp.AuthToken' : 'Abp.DevAuthToken';
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + this.getCookie(cookieName),
      'X-Webfacing': "true"
    });

    projectId ??= 0;
    calculationId ??= 0;

    return this.http.get(`${this.remoteServiceBaseUrl}/api/TokenAuth/ValidateWebFacingData`, {
      headers,
      params: { projectId, calculationId, calculationTypeEnum },
      withCredentials: true
    });
  }

  // Slow safety net only. Login/logout in another tab arrives instantly via AuthChangeSignal, and
  // returning to the tab triggers an immediate (throttled) check; this just catches silent
  // server-side changes while a tab sits idle and visible.
  private static readonly pollIntervalMs = 90000;
  private static readonly minCheckGapMs = 30000;
  private intervalId: any = null;
  private pollingEnabled = false;
  private lastCheckAt = 0;
  private unlistenAuthChange: (() => void) | null = null;
  private readonly onVisibilityChange = () => {
    // Timers are throttled and pages can be frozen while the tab is hidden, which aborts any
    // request left in flight. Nothing useful can happen in the background, so pause instead.
    if (document.hidden) {
      this.stopTimer();
    } else {
      this.startTimer();
      this.checkSessionStatusThrottled();
    }
  };
  private readonly onWindowFocus = () => this.checkSessionStatusThrottled();

  startPolling() {
    // Called from both RootComponent and the webfacing module - keep a single poller.
    if (this.pollingEnabled) {
      return;
    }

    this.pollingEnabled = true;
    // Stamp now so the startup visibility/focus events don't fire an early check (reload loop).
    this.lastCheckAt = Date.now();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
    // The message is not trusted; always re-ask the server.
    this.unlistenAuthChange = AuthChangeSignal.listen(() => this.checkSessionStatus());

    if (!document.hidden) {
      this.startTimer();
    }
  }

  stopPolling() {
    this.pollingEnabled = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
    this.unlistenAuthChange?.();
    this.unlistenAuthChange = null;
    this.stopTimer();
  }

  private checkSessionStatusThrottled() {
    if (Date.now() - this.lastCheckAt >= AuthService.minCheckGapMs) {
      this.checkSessionStatus();
    }
  }

  private startTimer() {
    this.stopTimer();
    // No immediate check on (re)start: the session may not be resolved yet at app startup and
    // an early reload() would loop.
    this.intervalId = setInterval(() => this.checkSessionStatus(), AuthService.pollIntervalMs);
  }

  private stopTimer() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private checkSessionStatus() {
    this.lastCheckAt = Date.now();
    // X-Skip-Interceptor: this is a background poll, a transient failure on it must never raise
    // the "Network Unreachable" modal. The endpoint is anonymous-safe and always answers 200,
    // so no 401/403/maintenance handling is lost by skipping the interceptor.
    const headers = new HttpHeaders({ 'X-Skip-Interceptor': 'true' });

    this.http.get<any>(`${this.remoteServiceBaseUrl}/api/TokenAuth/CheckSessionStatus`, { headers }).subscribe({
      next: res => {
        if (!res?.result) {
          return;
        }

        if (res.result.loggedIn && (!this._appSessionService.user || this._appSessionService.user?.isWebFacingUser)) {
          window.location.reload(); // or update auth state
        } else if (!res.result.loggedIn && this._appSessionService.user && !this._appSessionService.user?.isWebFacingUser) {
          window.location.reload();
        }
      },
      error: () => { /* transient - the next poll retries */ }
    });
  }

}
